import { Controller } from "@hotwired/stimulus";
import { getCsrfToken } from "common/utils/rails";

/**
 * Persists the "fonts will be used for: a client / myself" selection from inside the cart
 * address section.
 *
 * Reuses Store::LicensingController#select_options (PUT /licensing/select_options) so the
 * session-scoped `Current.license_owner_type` / `Current.license_owner_name` match what the
 * font-family page writes. The endpoint accepts a branding-only payload (no
 * `licensing_options_data`), leaving the cart's licensing selections intact.
 *
 * The `license-usage-label` controller on the same element keeps related labels in sync
 * across the page without any server round-trip.
 */
export default class extends Controller {
  connect() {
    this.onRadioChange = () => this.persist();
    this.onClientNameChange = () => this.persist();

    this.element
      .querySelectorAll('input[name="license_usage"]')
      .forEach((r) => r.addEventListener("change", this.onRadioChange));

    this.clientNameInput = this.element.querySelector(
      'input[name="license_usage_client_name"]'
    );
    this.clientNameInput?.addEventListener("change", this.onClientNameChange);
  }

  disconnect() {
    this.element
      .querySelectorAll('input[name="license_usage"]')
      .forEach((r) => r.removeEventListener("change", this.onRadioChange));
    this.clientNameInput?.removeEventListener(
      "change",
      this.onClientNameChange
    );
  }

  async persist() {
    const picked = /** @type {HTMLInputElement|null} */ (
      this.element.querySelector('input[name="license_usage"]:checked')
    );
    if (!picked) return;

    const ownerType = picked.value === "me" ? "me" : "client";
    const ownerName =
      ownerType === "client" ? this.clientNameInput?.value || "" : "";

    try {
      const response = await fetch("/licensing/select_options", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": getCsrfToken(),
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify({
          license_owner_type: ownerType,
          license_owner_name: ownerName,
        }),
      });
      if (!response.ok) {
        console.error(
          "cart-license-usage: persist failed",
          response.status,
          response.statusText
        );
      }
    } catch (err) {
      console.error("cart-license-usage: persist error", err);
    }
  }
}
